import { useState, useMemo, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Bed,
  User,
  Stethoscope,
  HeartPulse,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Phone,
  FileText,
  Plus,
  X,
  Search,
  Layers,
  Square,
  SquareCheck,
  ShoppingCart,
  Trash2,
  Info,
  AlertTriangle,
  Eye,
  CalendarClock,
  Globe,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PatientVisitSelector } from '../../components/ui/patient-visit-selector';
import { joinFullName, patientInitials } from '../../utils/patientName';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { QuickPatientModal } from '../../components/ui/quick-patient-modal';
import { ReferralSearchSelect } from '../../components/ui/ReferralSearchSelect';
import { QuickReferralModal } from '../../components/ui/QuickReferralModal';
import { InfoHint } from '../../components/ui/InfoHint';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { AdmissionInvoiceModal } from '../../components/ui/admission-invoice-modal';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  OutsideDoctorModal,
  type CreatedDoctor,
} from '../../components/ui/outside-doctor-modal';
import { QuantityStepper } from '../../components/ui/quantity-stepper';
import {
  defaultUnitLabel,
  isEmergencyService,
  isEmergencyServiceType,
  isQuantityBilled,
  normalizeServiceTypeCode,
  resolveServicePricing,
  round2,
  serviceQuantityBounds,
} from '../../utils/servicePricing';
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav';
import { usePermissions } from '../../hooks/usePermissions';
import {
  DEFAULT_HOSPITAL_TIMEZONE,
  datetimeLocalToISOInTimeZone,
  toDatetimeLocalValueInTimeZone,
} from '../../lib/timezones';
import { formatOrdinalDateTimeLong } from '../../utils/dateFormat';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DateTimePicker } from '../../components/ui/DatePicker';
import {
  useDoctors,
  useBeds,
  useCreateAdmission,
  useEmployees,
  usePatient,
  useWards,
  useServices,
  useActiveServiceTypes,
  useActiveAdmissionByPatient,
  useSettings,
} from '../../services/httpServices/queries';
import type { ReferralPartner } from '../../services/httpServices/referralService';

interface StaffAssignmentRow {
  employeeId: string;
  role: 'PRIMARY_DOCTOR' | 'DUTY_DOCTOR' | 'NURSE' | 'SURGEON' | 'ANESTHESIOLOGIST' | 'CONSULTANT' | 'OTHER';
  notes: string;
}

interface FormData {
  patientId: string;
  visitId: string;
  bedId: string;
  doctorId: string;
  admissionType: 'DIRECT' | 'REFERRAL' | 'EMERGENCY';
  admittedAt: string;
  admissionReason: string;
  referralDetails: string;
  referredById: string;
  /** IPD referral partner paid a flat commission on this stay. */
  referralId: string;
  /** Flat referral commission (৳) — not a percentage of the bill. */
  referralFlatAmount: number;
  serviceIds: string[];
  /** Billable units per service id (1 when the draft predates quantities). */
  serviceQuantities: Record<string, number>;
  /**
   * Per-unit price overwrites (৳) per service id. Only used for Emergency
   * service lines on an Emergency admission; every other line bills the
   * catalog price.
   */
  servicePriceOverrides: Record<string, number>;
  staffAssignments: StaffAssignmentRow[];
}

interface FormErrors {
  patientId?: string;
  visitId?: string;
  bedId?: string;
  doctorId?: string;
  admittedAt?: string;
  admissionReason?: string;
  serviceIds?: string;
}

/** A cart entry with its live, billable pricing preview. */
interface SelectedServiceLine {
  service: any;
  quantity: number;
  pricing: ReturnType<typeof resolveServicePricing>;
  /** True when the line's unit price was overwritten (emergency only). */
  priceOverridden: boolean;
  /** The catalog rate, kept so an override can be shown against it. */
  catalogUnitPrice: number;
}

const formatMoney = (value: number) =>
  `৳${(Number.isFinite(value) ? value : 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

const admissionTypes = [
  { key: 'DIRECT' as const, label: 'Direct', icon: ArrowRight, color: 'bg-primary/10 text-primary border-primary/20' },
  { key: 'REFERRAL' as const, label: 'Referral', icon: FileText, color: 'bg-secondary/10 text-secondary border-secondary/20' },
  { key: 'EMERGENCY' as const, label: 'Emergency', icon: AlertCircle, color: 'bg-danger/10 text-danger border-danger/20' },
];

/** Compact card heading used by every card on the admission page. */
function CardHeader({
  icon: Icon,
  title,
  description,
  badge,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  badge?: ReactNode;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/10">
        <Icon size={15} />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-sm font-semibold leading-tight text-text">{title}</h2>
        {description && (
          <p className="mt-0.5 truncate text-[11px] leading-tight text-text-muted">{description}</p>
        )}
      </div>
      {badge}
    </div>
  );
}

/** Small state badge shown in a card header. */
function Pill({
  tone = 'muted',
  children,
}: {
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'muted';
  children: ReactNode;
}) {
  const tones = {
    primary: 'bg-primary/10 text-primary ring-primary/20',
    success: 'bg-success/10 text-success ring-success/20',
    warning: 'bg-warning/10 text-warning ring-warning/20',
    danger: 'bg-danger/10 text-danger ring-danger/20',
    muted: 'bg-background text-text-muted ring-border',
  };
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-4 ring-1 ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

/** Compact icon + label/value cell used by the admission summary grid. */
function SummaryCell({
  icon: Icon,
  label,
  value,
  sub,
  valueClassName = 'text-text',
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex min-w-0 items-start gap-2 bg-surface p-2.5">
      <Icon size={13} className="mt-0.5 shrink-0 text-text-muted" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{label}</p>
        <p className={`truncate text-xs font-semibold ${valueClassName}`}>{value}</p>
        {sub && <p className="truncate text-[10px] leading-relaxed text-text-muted">{sub}</p>}
      </div>
    </div>
  );
}

/** Temporary draft persisted in localStorage while the form is in progress. */
const ADMISSION_DRAFT_KEY = 'firstaid:ipd-admission-draft';

interface AdmissionDraft {
  formData: FormData;
  selectedWardId: string;
}

function createEmptyFormData(timeZone?: string): FormData {
  return {
    patientId: '',
    visitId: '',
    bedId: '',
    doctorId: '',
    admissionType: 'DIRECT',
    admittedAt: toDatetimeLocalValueInTimeZone(new Date(), timeZone),
    admissionReason: '',
    referralDetails: '',
    referredById: '',
    referralId: '',
    referralFlatAmount: 0,
    serviceIds: [],
    serviceQuantities: {},
    servicePriceOverrides: {},
    staffAssignments: [],
  };
}

function readAdmissionDraft(): Partial<AdmissionDraft> | null {
  try {
    const raw = localStorage.getItem(ADMISSION_DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Partial<AdmissionDraft>) : null;
  } catch {
    return null;
  }
}

function clearAdmissionDraft() {
  try {
    localStorage.removeItem(ADMISSION_DRAFT_KEY);
  } catch {
    /* ignore storage errors */
  }
}

/**
 * Restores a persisted draft. Drafts written before the service cart tracked
 * quantities have no `serviceQuantities`, and ids that are no longer in the
 * cart are dropped so the totals never include a stale quantity. Price
 * overwrites follow the same rule: they only survive for a service still in
 * the cart.
 */
function restoreAdmissionFormData(draft?: Partial<FormData>): FormData {
  const restored: FormData = { ...createEmptyFormData(), ...(draft ?? {}) };
  const serviceIds = Array.isArray(restored.serviceIds)
    ? restored.serviceIds.filter((id): id is string => typeof id === 'string')
    : [];
  const quantities = restored.serviceQuantities ?? {};
  const overrides = restored.servicePriceOverrides ?? {};

  restored.serviceIds = serviceIds;
  restored.serviceQuantities = Object.fromEntries(
    serviceIds
      .map((id) => [id, Number(quantities[id])] as const)
      .filter(([, quantity]) => Number.isFinite(quantity) && quantity > 0),
  );
  restored.servicePriceOverrides = Object.fromEntries(
    serviceIds
      .map((id) => [id, Number(overrides[id])] as const)
      .filter(([, price]) => Number.isFinite(price) && price >= 0),
  );
  return restored;
}

/** True when nothing meaningful has been entered yet, so the draft can be dropped. */
function isAdmissionFormEmpty(form: FormData, wardId: string): boolean {
  return (
    !form.patientId &&
    !form.visitId &&
    !form.bedId &&
    !form.doctorId &&
    !form.admissionReason.trim() &&
    !form.referralDetails.trim() &&
    !form.referredById &&
    !form.referralId &&
    !Number(form.referralFlatAmount) &&
    form.serviceIds.length === 0 &&
    form.staffAssignments.length === 0 &&
    !wardId
  );
}

export function AdmissionForm() {
  const navigate = useNavigate();

  const { data: doctorsData, isLoading: doctorsLoading } = useDoctors(1, 100);
  const { data: bedsData, isLoading: bedsLoading } = useBeds('AVAILABLE');
  const { data: employeesData, isLoading: employeesLoading } = useEmployees(1, 200);
  const { data: wardsData, isLoading: wardsLoading } = useWards(1, 100);
  const { data: servicesData, isLoading: servicesLoading } = useServices(1, 200);
  const { data: serviceTypesData } = useActiveServiceTypes();
  const createAdmission = useCreateAdmission();

  // Only roles granted `ipd:admission:backdate` (Roles → Permissions) may see or
  // edit the admission timestamp. Everyone else gets the current instant from
  // the server, set automatically.
  const { hasPermission } = usePermissions();
  const canEditAdmissionTime = hasPermission('ipd:admission:backdate');

  // Hospital timezone configured on Settings → General (`hospital_timezone`).
  // Stays `undefined` until the settings request settles so the initial seed
  // below runs with the configured zone (or the shared Asia/Dhaka fallback)
  // instead of the browser's zone.
  const { data: settingsData } = useSettings();
  const hospitalTimezone = useMemo(() => {
    if (!settingsData) return undefined;
    const value = settingsData.data?.find((s: any) => s.key === 'hospital_timezone')?.value;
    return (typeof value === 'string' ? value.trim() : '') || DEFAULT_HOSPITAL_TIMEZONE;
  }, [settingsData]);

  // Guards the one-time seed of the admission timestamp so a later settings
  // refetch (e.g. after switching tabs) can't overwrite a manually chosen time.
  const seededAdmissionTimeRef = useRef(false);

  const [formData, setFormData] = useState<FormData>(() =>
    restoreAdmissionFormData(readAdmissionDraft()?.formData),
  );

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [selectedWardId, setSelectedWardId] = useState<string>(
    () => readAdmissionDraft()?.selectedWardId ?? '',
  );
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Whether a saved draft was restored on mount. Drives the "start fresh" prompt.
  const [hasSavedDraft, setHasSavedDraft] = useState(() => !!readAdmissionDraft());
  const [showClearDialog, setShowClearDialog] = useState(false);

  // IPD referral partner (flat commission payee). The name/type are display-only
  // mirrors of the selected partner; the id + flat amount are what we persist.
  const [referralName, setReferralName] = useState('');
  const [referralType, setReferralType] = useState<'DOCTOR' | 'EMPLOYEE' | 'OTHER' | ''>('');
  const [referralTypeFilter, setReferralTypeFilter] = useState<
    'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER'
  >('ALL');
  const [showQuickReferralModal, setShowQuickReferralModal] = useState(false);
  const [quickReferralQuery, setQuickReferralQuery] = useState('');
  // A partner with a 0 amount books no payout; ask before saving such a stay.
  const [showReferralZeroWarning, setShowReferralZeroWarning] = useState(false);

  // Doctors created inline via the "+ Add Outside Doctor" action. Kept locally so
  // they appear (and resolve their label) immediately, before the doctors list
  // refetches after creation.
  const [outsideDoctors, setOutsideDoctors] = useState<{ id: string; name: string }[]>([]);
  const [showOutsideDoctorModal, setShowOutsideDoctorModal] = useState(false);
  // Quick patient registration dialog opened from the "Add new patient" action in
  // the patient selector. `patientQuery` carries the name/mobile typed there.
  const [showQuickPatientModal, setShowQuickPatientModal] = useState(false);
  const [patientQuery, setPatientQuery] = useState('');
  // Which field the "Add Outside Doctor" action was triggered from, so the newly
  // created doctor is assigned to that field (primary doctor vs. referred by).
  const [outsideDoctorTarget, setOutsideDoctorTarget] = useState<'doctorId' | 'referredById'>('doctorId');
  // Text typed into the doctor search box when the quick-entry dialog was opened;
  // seeded as the new doctor's name (or phone when only digits were typed).
  const [outsideDoctorQuery, setOutsideDoctorQuery] = useState('');

  const { data: patientData } = usePatient(formData.patientId);
  const patient = patientData?.data;

  // A patient may only have one active stay at a time. Fetch any open admission
  // for the selected patient so we can block a duplicate and show its details.
  const { data: activeAdmissionData, isFetching: activeAdmissionLoading } =
    useActiveAdmissionByPatient(formData.patientId || undefined);
  const activeAdmission = activeAdmissionData?.data || null;
  const [showActiveAdmissionModal, setShowActiveAdmissionModal] = useState(false);
  // The admission created by the last submit, kept so the post-admission invoice
  // dialog can bill it before leaving the page.
  const [createdAdmission, setCreatedAdmission] = useState<any>(null);
  const [showBillingModal, setShowBillingModal] = useState(false);

  const doctors = doctorsData?.data || [];
  const beds = bedsData?.data || [];
  const employees = employeesData?.data || [];
  const wards = wardsData?.data || [];
  const services = (servicesData?.data || []).filter((s: any) => s.isActive !== false);

  const doctorOptions = useMemo(() => {
    const base = doctors.map((d: any) => ({
      value: d.id,
      label: d.name || d.employeeCode || d.id,
    }));
    // Include doctors created inline (outside doctors) even before the server
    // list refreshes, so the selection renders with the right name.
    const known = new Set(base.map((o) => o.value));
    const extras = outsideDoctors
      .filter((d) => !known.has(d.id))
      .map((d) => ({ value: d.id, label: d.name }));
    return [...extras, ...base];
  }, [doctors, outsideDoctors]);

  const employeeOptions = useMemo(() =>
    employees.map((e: any) => ({
      value: e.id,
      label: `${e.name || e.employeeCode || e.id}${e.designation?.name ? ` (${e.designation.name})` : ''}`,
    })),
  [employees]);

  const wardOptions = useMemo(() => {
    // Count available beds per ward so every active ward is listed even when it
    // currently has no free bed (the bed grid below shows the availability).
    const availableByWard = new Map<string, number>();
    beds.forEach((b: any) => {
      if (b.wardId) availableByWard.set(b.wardId, (availableByWard.get(b.wardId) ?? 0) + 1);
    });
    return wards
      .filter((w: any) => w.isActive !== false)
      .map((w: any) => {
        const available = availableByWard.get(w.id) ?? 0;
        return {
          value: w.id,
          label: `${w.wardName} (${w.wardType.replace('_', ' ')})`,
          description: available > 0 ? `${available} bed${available === 1 ? '' : 's'} available` : 'No available beds',
        };
      });
  }, [wards, beds]);

  const selectedWard = useMemo(() => wards.find((w: any) => w.id === selectedWardId), [wards, selectedWardId]);
  const wardBedsForSelected = useMemo(() => {
    if (!selectedWardId) return [];
    return beds.filter((b: any) => b.wardId === selectedWardId && b.status === 'AVAILABLE');
  }, [beds, selectedWardId]);
  const isAutoSelectedBed = useMemo(() => {
    return selectedWard?.wardType === 'PRIVATE' && wardBedsForSelected.length === 1 && formData.bedId === wardBedsForSelected[0]?.id;
  }, [selectedWard, wardBedsForSelected, formData.bedId]);

  // Emergency admissions go straight into care without a bed, so the whole Bed
  // Assignment panel is locked for that admission type.
  const isEmergencyAdmission = formData.admissionType === 'EMERGENCY';

  const selectedBed = useMemo(
    () => beds.find((b: any) => b.id === formData.bedId),
    [beds, formData.bedId],
  );
  const selectedDoctor = useMemo(
    () =>
      doctors.find((d: any) => d.id === formData.doctorId) ??
      outsideDoctors.find((d) => d.id === formData.doctorId),
    [doctors, outsideDoctors, formData.doctorId],
  );
  const selectedReferredBy = useMemo(() => doctors.find((d: any) => d.id === formData.referredById), [doctors, formData.referredById]);

  const selectedStaffEmployees = useMemo(() => {
    return formData.staffAssignments.map((s) => ({
      ...s,
      employee: employees.find((e: any) => e.id === s.employeeId),
    }));
  }, [formData.staffAssignments, employees]);

  // Active service types (from the API) power the "by type" filter and let the
  // search match the human-readable type name in addition to the stored code.
  const serviceTypes = serviceTypesData?.data || [];
  const serviceTypeName = (code?: string) =>
    serviceTypes.find((type: any) => type.code === code)?.name || code || '';

  const filteredServices = useMemo(() => {
    const q = serviceSearch.trim().toLowerCase();
    return services.filter((s: any) => {
      if (serviceTypeFilter !== 'all' && s.serviceType !== serviceTypeFilter) return false;
      if (!q) return true;
      const haystack = [
        s.name,
        s.code,
        s.serviceType,
        s.serviceType ? serviceTypeName(s.serviceType) : '',
        s.description,
        s.departmentCode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, serviceSearch, serviceTypeFilter, serviceTypes]);

  // Codes of the managed "Emergency" Service Type. Only these lines may have
  // their price overwritten, and only on an emergency admission.
  const emergencyServiceTypeCodes = useMemo(() => {
    const codes = new Set<string>();
    (serviceTypesData?.data || []).forEach((type: any) => {
      if (isEmergencyServiceType(type?.code, type?.name)) {
        codes.add(normalizeServiceTypeCode(type?.code));
      }
    });
    return codes;
  }, [serviceTypesData]);

  /** True when the cart may overwrite this line's unit price. */
  const canOverrideServicePrice = (service: any) =>
    isEmergencyAdmission &&
    isEmergencyService(service?.serviceType, emergencyServiceTypeCodes);

  const selectedLines = useMemo<SelectedServiceLine[]>(() => {
    return formData.serviceIds
      .map((id) => {
        const service = services.find((s: any) => s.id === id);
        if (!service) return null;
        const quantity = formData.serviceQuantities[id] ?? 1;
        const override = formData.servicePriceOverrides[id];
        // A stale override (e.g. the admission is no longer an emergency) is
        // ignored so the line never silently bills an unapproved price.
        const priceOverridden =
          override != null &&
          isEmergencyAdmission &&
          isEmergencyService(service.serviceType, emergencyServiceTypeCodes);
        const priced = priceOverridden ? { ...service, price: override } : service;
        return {
          service,
          quantity,
          pricing: resolveServicePricing(priced, quantity),
          catalogUnitPrice: resolveServicePricing(service, quantity).unitPrice,
          priceOverridden,
        };
      })
      .filter(Boolean) as SelectedServiceLine[];
  }, [
    formData.serviceIds,
    formData.serviceQuantities,
    formData.servicePriceOverrides,
    services,
    isEmergencyAdmission,
    emergencyServiceTypeCodes,
  ]);

  const selectedServices = useMemo(
    () => selectedLines.map((line) => line.service),
    [selectedLines],
  );

  // Total mirrors the backend pricing rules (minimum units + hourly rounding),
  // excluding tax — the invoice adds tax when the charges are billed.
  const servicesTotal = useMemo(
    () => round2(selectedLines.reduce((sum, line) => sum + line.pricing.amount, 0)),
    [selectedLines],
  );

  const toggleService = (id: string) => {
    setFormData((prev) => {
      if (prev.serviceIds.includes(id)) {
        const serviceQuantities = { ...prev.serviceQuantities };
        const servicePriceOverrides = { ...prev.servicePriceOverrides };
        delete serviceQuantities[id];
        delete servicePriceOverrides[id];
        return {
          ...prev,
          serviceIds: prev.serviceIds.filter((x) => x !== id),
          serviceQuantities,
          servicePriceOverrides,
        };
      }
      const service = services.find((s: any) => s.id === id);
      return {
        ...prev,
        serviceIds: [...prev.serviceIds, id],
        // Seed the line with its billable minimum rather than a bare 1.
        serviceQuantities: {
          ...prev.serviceQuantities,
          [id]: service ? resolveServicePricing(service, 1).quantity : 1,
        },
      };
    });
  };

  /** Applies the service's min/rounding rules so the preview matches billing. */
  const setServiceQuantity = (id: string, rawQuantity: number) => {
    setFormData((prev) => {
      const service = services.find((s: any) => s.id === id);
      if (!service || !prev.serviceIds.includes(id)) return prev;
      const bounds = serviceQuantityBounds(service);
      const quantity = Math.min(
        bounds.max,
        Math.max(bounds.min, resolveServicePricing(service, rawQuantity).quantity),
      );
      return {
        ...prev,
        serviceQuantities: { ...prev.serviceQuantities, [id]: quantity },
      };
    });
  };

  const clearServices = () => {
    setFormData((prev) => ({
      ...prev,
      serviceIds: [],
      serviceQuantities: {},
      servicePriceOverrides: {},
    }));
  };

  /**
   * Overwrites one unit price (emergency lines only). Setting it back to the
   * catalog rate drops the override, so the line is billed like any other.
   */
  const setServicePrice = (id: string, rawPrice: number) => {
    setFormData((prev) => {
      if (!prev.serviceIds.includes(id)) return prev;
      const service = services.find((s: any) => s.id === id);
      const price = round2(Math.max(0, Number(rawPrice) || 0));
      const servicePriceOverrides = { ...prev.servicePriceOverrides };
      if (service && price === round2(Number(service.price) || 0)) {
        delete servicePriceOverrides[id];
      } else {
        servicePriceOverrides[id] = price;
      }
      return { ...prev, servicePriceOverrides };
    });
  };

  /** Drops the overwrite so the line bills the catalog price again. */
  const resetServicePrice = (id: string) => {
    setFormData((prev) => {
      const servicePriceOverrides = { ...prev.servicePriceOverrides };
      delete servicePriceOverrides[id];
      return { ...prev, servicePriceOverrides };
    });
  };

  // Up/Down moves through the service list, Enter toggles the highlighted one.
  const serviceNav = useListKeyboardNav({
    itemCount: filteredServices.length,
    onSelect: (index) => {
      const service = filteredServices[index];
      if (service) toggleService(service.id);
    },
    resetKey: serviceSearch,
  });

  const validate = (): boolean => {
    const nextErrors: FormErrors = {};
    if (!formData.patientId) nextErrors.patientId = 'Patient is required';
    if (activeAdmission) nextErrors.patientId = 'Patient already has an active admission';
    // No visit is required: the admission opens its own IPD visit server-side,
    // keeping the stay's bill separate from any OPD encounter.
    // A bed is optional only for emergency admissions.
    if (!isEmergencyAdmission && !formData.bedId) nextErrors.bedId = 'Bed is required';
    if (!formData.admittedAt) nextErrors.admittedAt = 'Admission date & time is required';
    if (!formData.admissionReason?.trim()) nextErrors.admissionReason = 'Reason for admission is required';
    // Every admission must carry at least one billable service so the visit is
    // never opened without a charge behind it.
    if (formData.serviceIds.length === 0)
      nextErrors.serviceIds = 'At least one service is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  useEffect(() => {
    if (Object.keys(touched).length > 0) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Open the blocking modal automatically as soon as an active admission is
  // found for the selected patient, so the duplicate is never missed. Skip the
  // admission that was just created by this form — the post-admission billing
  // dialog is handling it instead.
  useEffect(() => {
    if (activeAdmission && activeAdmission.id !== createdAdmission?.id) {
      setShowActiveAdmissionModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAdmission?.id, createdAdmission?.id]);

  // Persist the in-progress draft on every change so a refresh keeps the data.
  // When the form is emptied (e.g. "start fresh"), drop the draft entirely.
  useEffect(() => {
    try {
      if (isAdmissionFormEmpty(formData, selectedWardId)) {
        clearAdmissionDraft();
        return;
      }
      localStorage.setItem(
        ADMISSION_DRAFT_KEY,
        JSON.stringify({
          formData,
          selectedWardId,
        } satisfies AdmissionDraft),
      );
    } catch {
      /* ignore storage errors */
    }
  }, [formData, selectedWardId]);

  // Every time the form is opened, default the admission timestamp to the
  // current wall-clock time in the hospital timezone. This overrides any
  // previously drafted timestamp (which belongs to an earlier attempt) but
  // still lets an admin edit the value afterwards. Runs once, so a later
  // settings refetch can't clobber a manually chosen time.
  useEffect(() => {
    if (!hospitalTimezone) return;
    if (seededAdmissionTimeRef.current) return;
    seededAdmissionTimeRef.current = true;
    setFormData((prev) => ({
      ...prev,
      admittedAt: toDatetimeLocalValueInTimeZone(new Date(), hospitalTimezone),
    }));
  }, [hospitalTimezone]);

  const resetForm = () => {
    clearAdmissionDraft();
    setFormData(createEmptyFormData(hospitalTimezone));
    setSelectedWardId('');
    setErrors({});
    setTouched({});
    setSubmitAttempted(false);
    setServiceSearch('');
    setServiceTypeFilter('all');
    setOutsideDoctors([]);
    setOutsideDoctorQuery('');
    setReferralName('');
    setReferralType('');
    setReferralTypeFilter('ALL');
    setHasSavedDraft(false);
    setShowClearDialog(false);
  };

  /** Effective flat commission: never negative. */
  // Referral payouts are whole taka — no paisa in the payout ledger.
  const referralFlatAmount = Math.max(
    0,
    Math.round(Number(formData.referralFlatAmount) || 0),
  );
  const referralPayoutIsZero = !!formData.referralId && referralFlatAmount <= 0;
  const isSubmitting = createAdmission.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSubmit();
  };

  /**
   * Warn before saving a stay that is linked to a partner but pays ৳0 — that
   * admission would never appear in Billing → Payouts.
   */
  const performSubmit = () => {
    setSubmitAttempted(true);
    setTouched({ patientId: true, visitId: true, bedId: true, admittedAt: true, admissionReason: true, serviceIds: true });
    if (!validate()) {
      if (activeAdmission) setShowActiveAdmissionModal(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (referralPayoutIsZero) {
      setShowReferralZeroWarning(true);
      return;
    }
    submitAdmission();
  };

  const submitAdmission = () => {
    setShowReferralZeroWarning(false);

    // The referral snapshot is saved with the admission.
    const referralPayload = {
      referralId: formData.referralId || null,
      referralFlatAmount: formData.referralId ? referralFlatAmount : 0,
    };

    const payload = {
      patientId: formData.patientId,
      // Optional source encounter; the server always opens a dedicated IPD
      // visit for the stay and links this one as the parent for continuity.
      visitId: formData.visitId || undefined,
      // Emergency admissions are created without a bed; it can be assigned later.
      bedId: isEmergencyAdmission ? undefined : formData.bedId,
      doctorId: formData.doctorId || undefined,
      admissionType: formData.admissionType,
      // Roles granted `ipd:admission:backdate` choose the time themselves;
      // interpret their wall-clock in the hospital timezone. Every other role
      // omits the field so the server stamps the actual submit instant (which
      // must never be rejected as "backdated" after a long form-filling pause).
      admittedAt: canEditAdmissionTime
        ? datetimeLocalToISOInTimeZone(formData.admittedAt, hospitalTimezone)
        : undefined,
      admissionReason: formData.admissionReason,
      referralDetails: formData.admissionType === 'REFERRAL' ? formData.referralDetails : undefined,
      referredById: formData.referredById || undefined,
      ...referralPayload,
      // Send both shapes so older API builds (id-only) and current builds
      // (quantity-aware) bill the same cart.
      serviceIds: formData.serviceIds,
      services: selectedLines.map(({ service, pricing, priceOverridden }) => ({
        serviceId: service.id,
        quantity: pricing.quantity,
        // Emergency lines on an emergency admission may carry an overwritten
        // unit price; the server rejects an override anywhere else.
        ...(priceOverridden ? { unitPrice: pricing.unitPrice } : {}),
      })),
      staffAssignments: formData.staffAssignments
        .filter((s) => s.employeeId)
        .map((s) => ({
          employeeId: s.employeeId,
          role: s.role,
          notes: s.notes || undefined,
        })),
    };

    createAdmission.mutate(payload as any, {
      onSuccess: (response: any) => {
        clearAdmissionDraft();
        // Stay on the page and offer the billing choice (invoice or GO). The
        // dialog navigates away once the user is done or skips it.
        setCreatedAdmission(response?.data ?? null);
        setShowBillingModal(true);
      },
    });
  };

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  /** Switching to Emergency clears and locks the bed assignment. */
  const handleAdmissionTypeChange = (type: FormData['admissionType']) => {
    const emergency = type === 'EMERGENCY';
    setFormData((prev) => ({
      ...prev,
      admissionType: type,
      bedId: emergency ? '' : prev.bedId,
      // Overwritten prices only exist for emergency service lines, so they are
      // dropped as soon as the stay is no longer an emergency admission.
      servicePriceOverrides: emergency ? prev.servicePriceOverrides : {},
    }));
    if (emergency) setSelectedWardId('');
    setTouched((prev) => ({ ...prev, admissionType: true, bedId: true }));
  };

  const handleWardChange = (wardId: string) => {
    setSelectedWardId(wardId);
    if (!wardId) {
      setFormData((prev) => ({ ...prev, bedId: '' }));
      return;
    }
    const ward = wards.find((w: any) => w.id === wardId);
    const availableBedsInWard = beds.filter((b: any) => b.wardId === wardId && b.status === 'AVAILABLE');
    if (ward?.wardType === 'PRIVATE' && availableBedsInWard.length === 1) {
      setFormData((prev) => ({ ...prev, bedId: availableBedsInWard[0].id }));
    } else {
      setFormData((prev) => ({ ...prev, bedId: '' }));
    }
    setTouched((prev) => ({ ...prev, bedId: true }));
  };

  const addStaffAssignment = () => {
    setFormData((prev) => ({
      ...prev,
      staffAssignments: [...prev.staffAssignments, { employeeId: '', role: 'NURSE', notes: '' }],
    }));
  };

  const updateStaffAssignment = (index: number, field: keyof StaffAssignmentRow, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.staffAssignments];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, staffAssignments: updated };
    });
  };

  const removeStaffAssignment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      staffAssignments: prev.staffAssignments.filter((_, i) => i !== index),
    }));
  };

  /** Opens the quick-entry dialog for the doctor field that was clicked. */
  const openOutsideDoctor = (target: 'doctorId' | 'referredById', query: string) => {
    setOutsideDoctorTarget(target);
    setOutsideDoctorQuery(query);
    setShowOutsideDoctorModal(true);
  };

  /** Selects the doctor just created in the quick-entry dialog. */
  const handleOutsideDoctorCreated = (doctor: CreatedDoctor) => {
    setOutsideDoctors((prev) => [...prev, doctor]);
    setFormData((prev) => ({ ...prev, [outsideDoctorTarget]: doctor.id }));
  };

  const getPatientAge = (dob?: string) => {
    if (!dob) return null;
    const diff = Date.now() - new Date(dob).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  };

  /* ---------- Form sections ---------- */

  const patientStep = (
    <div className="flex h-full flex-col gap-3">
      <CardHeader
        icon={User}
        title="Patient"
        badge={
          activeAdmission ? (
            <Pill tone="danger">Already admitted</Pill>
          ) : formData.patientId ? (
            <Pill tone="success">
              <CheckCircle2 size={10} />
              Ready
            </Pill>
          ) : (
            <Pill>Required</Pill>
          )
        }
      />
      <PatientVisitSelector
        compact
        hideVisit
        patientId={formData.patientId}
        visitId={formData.visitId}
        errorMessage={!activeAdmission ? errors.patientId : undefined}
        onChange={({ patientId, visitId }) => {
          setFormData((prev) => ({ ...prev, patientId, visitId, bedId: '' }));
          setSelectedWardId('');
          setTouched((prev) => ({ ...prev, patientId: true }));
        }}
        onAddNewPatient={(query) => {
          setPatientQuery(query);
          setShowQuickPatientModal(true);
        }}
      />
      <p className="text-[11px] leading-snug text-text-muted">
        A dedicated IPD visit is opened automatically on save, so this stay's
        bill stays separate from any OPD bill.
      </p>

      {activeAdmissionLoading && formData.patientId && (
        <div className="flex items-center gap-1.5 text-[11px] text-text-muted">
          <Loader2 size={12} className="animate-spin" />
          Checking existing admission...
        </div>
      )}

      {activeAdmission && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 px-2.5 py-2">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-danger" />
          <div className="min-w-0 flex-1 text-[11px] leading-snug">
            <p className="font-semibold text-danger">
              Patient already admitted
              {activeAdmission.bed?.bedNumber ? ` · bed ${activeAdmission.bed.bedNumber}` : ''}
            </p>
            <button
              type="button"
              onClick={() => setShowActiveAdmissionModal(true)}
              className="mt-0.5 font-semibold text-primary transition-colors hover:text-primary-dark cursor-pointer"
            >
              View existing admission
            </button>
          </div>
        </div>
      )}

      {patient ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-border bg-background/60 px-2.5 py-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
            {joinFullName(patient)
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((part) => part.charAt(0))
              .join('')
              .toUpperCase() || '?'}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold leading-tight text-text">
              {joinFullName(patient)}
            </p>
            <p className="truncate text-[11px] leading-tight text-text-muted">
              {[
                `${getPatientAge(patient.dateOfBirth) ?? '—'} yrs`,
                patient.gender || '—',
                patient.bloodGroup,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
          {patient.mobileNumber && (
            <span className="flex max-w-[45%] shrink-0 items-center gap-1 truncate text-[11px] text-text-muted">
              <Phone size={11} className="shrink-0" />
              <span className="truncate">{patient.mobileNumber}</span>
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border px-2.5 py-2 text-[11px] text-text-muted">
          <User size={13} className="shrink-0" />
          Patient details appear after selection.
        </div>
      )}
    </div>
  );

  const bedSection = (
    <div className="flex h-full flex-col gap-3">
      <CardHeader
        icon={Bed}
        title="Bed Assignment"
        description={
          isEmergencyAdmission ? 'Not required for emergency admissions' : undefined
        }
        badge={
          isEmergencyAdmission ? (
            <Pill>Not required</Pill>
          ) : selectedBed ? (
            <Pill tone="primary">
              <CheckCircle2 size={10} />
              Bed {selectedBed.bedNumber}
            </Pill>
          ) : selectedWardId ? (
            <Pill tone="success">{wardBedsForSelected.length} free</Pill>
          ) : (
            <Pill>Required</Pill>
          )
        }
      />
      {isEmergencyAdmission && (
        <div className="flex items-start gap-2 rounded-lg border border-warning/20 bg-warning/10 px-2.5 py-2 text-[11px] leading-snug text-warning">
          <Info size={13} className="mt-0.5 shrink-0" />
          <span>
            Emergency admittance — bed assignment is disabled. A bed can be assigned later
            when the patient is moved to a ward.
          </span>
        </div>
      )}
      {bedsLoading || wardsLoading ? (
        <div className="flex flex-1 items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <div
          aria-disabled={isEmergencyAdmission}
          className={`flex flex-1 flex-col gap-3 ${
            isEmergencyAdmission ? 'pointer-events-none opacity-60 select-none' : ''
          }`}
        >
          {/* Emergency admissions don't need a bed, so an empty bed list is not a problem there. */}
          {!isEmergencyAdmission && beds.length === 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/20 bg-warning/10 px-2.5 py-2 text-[11px] leading-snug text-warning">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              No available beds. Configure beds or wait for a discharge.
            </div>
          )}
          <SearchableSelect
            label={
              <span className="flex items-baseline justify-between gap-2">
                <span>Ward *</span>
                {errors.bedId && (
                  <span className="shrink-0 text-right text-xs font-medium text-danger">
                    {errors.bedId}
                  </span>
                )}
              </span>
            }
            labelClassName="block text-sm font-medium text-text mb-1.5"
            // Message is shown in the label row; passing error only keeps the danger border.
            error={errors.bedId ? <span className="sr-only" /> : undefined}
            value={selectedWardId}
            onChange={handleWardChange}
            options={wardOptions}
            placeholder="Select ward..."
            loading={wardsLoading}
            disabled={isEmergencyAdmission}
          />

          {selectedWardId ? (
            <div className="space-y-2">
              {wardBedsForSelected.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border px-2.5 py-3 text-center text-[11px] text-text-muted">
                  No available beds in this ward.
                </p>
              ) : (
                <div className="grid max-h-52 grid-cols-3 gap-1.5 overflow-y-auto pr-0.5">
                  {wardBedsForSelected.map((bed: any) => {
                    const isSelected = formData.bedId === bed.id;
                    return (
                      <button
                        key={bed.id}
                        type="button"
                        disabled={isAutoSelectedBed || isEmergencyAdmission}
                        onClick={() => updateField('bedId', bed.id)}
                        title={`${bed.bedNumber} · ${bed.type}${bed.room ? ` · Room ${bed.room}` : ''}`}
                        className={`relative flex flex-col items-center justify-center gap-0.5 rounded-lg border px-1.5 py-2 transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/10 ring-1 ring-primary/25'
                            : 'border-border bg-background/50 hover:border-primary/40 hover:bg-primary/5'
                        } ${
                          isAutoSelectedBed || isEmergencyAdmission
                            ? 'cursor-not-allowed'
                            : 'cursor-pointer'
                        }`}
                      >
                        <Bed
                          size={13}
                          className={isSelected ? 'text-primary' : 'text-text-muted'}
                        />
                        <span
                          className={`w-full truncate text-center text-xs font-semibold leading-tight ${
                            isSelected ? 'text-primary' : 'text-text'
                          }`}
                        >
                          {bed.bedNumber}
                        </span>
                        <span className="w-full truncate text-center text-[10px] capitalize leading-tight text-text-muted">
                          {bed.type?.replace(/_/g, ' ').toLowerCase() || '—'}
                        </span>
                        {isSelected && (
                          <CheckCircle2 size={11} className="absolute right-1 top-1 text-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {isAutoSelectedBed && (
                <p className="flex items-center gap-1 text-[11px] text-success">
                  <CheckCircle2 size={12} />
                  Auto-selected for single-bed private ward
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-border px-2.5 py-2 text-[11px] text-text-muted">
              <Bed size={13} className="shrink-0" />
              Select a ward to see its available beds.
            </div>
          )}
        </div>
      )}
    </div>
  );

  const teamSection = (
    <div className="flex h-full flex-col gap-3">
      <CardHeader
        icon={Stethoscope}
        title="Clinical Team"
        badge={
          formData.staffAssignments.length > 0 ? (
            <Pill tone="warning">
              <HeartPulse size={10} />
              {formData.staffAssignments.length} staff
            </Pill>
          ) : (
            <Pill>Optional</Pill>
          )
        }
      />
      <SearchableSelect
        label={
          <>
            Primary doctor <span className="font-normal text-text-muted">· optional</span>
          </>
        }
        labelClassName="block text-sm font-medium text-text mb-1.5"
        value={formData.doctorId}
        onChange={(value) => updateField('doctorId', value)}
        options={doctorOptions}
        placeholder="Select or add outside doctor..."
        loading={doctorsLoading}
        error={errors.doctorId}
        onAddNew={(query) => openOutsideDoctor('doctorId', query)}
        addNewLabel="Add Outside Doctor"
      />
      {formData.doctorId && outsideDoctors.some((d) => d.id === formData.doctorId) && (
        <p className="flex items-center gap-1 text-[11px] text-success">
          <CheckCircle2 size={12} />
          Outside doctor added
        </p>
      )}

      <div className="mt-0.5 border-t border-border pt-2.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-text">
            Additional staff <span className="font-normal text-text-muted">· optional</span>
          </p>
          <button
            type="button"
            onClick={addStaffAssignment}
            className="flex shrink-0 items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/20"
          >
            <Plus size={11} />
            Add
          </button>
        </div>

        {formData.staffAssignments.length > 0 ? (
          <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto pr-0.5">
            {formData.staffAssignments.map((assignment, index) => (
              <div key={index} className="flex items-center gap-1.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-[10px] font-bold text-primary">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <SearchableSelect
                    value={assignment.employeeId}
                    onChange={(value) => updateStaffAssignment(index, 'employeeId', value)}
                    options={employeeOptions}
                    placeholder="Select employee..."
                    loading={employeesLoading}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeStaffAssignment(index)}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                  title="Remove staff"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-dashed border-border px-2.5 py-2 text-[11px] text-text-muted">
            <HeartPulse size={13} className="shrink-0" />
            No support staff added.
          </div>
        )}
      </div>
    </div>
  );

  const detailsStep = (
    <div className="space-y-3">
      <CardHeader
        icon={FileText}
        title="Admission Details"
        description="Type, timing and the clinical reason."
        badge={
          <Pill
            tone={
              formData.admissionType === 'EMERGENCY'
                ? 'danger'
                : formData.admissionType === 'REFERRAL'
                  ? 'warning'
                  : 'primary'
            }
          >
            {admissionTypes.find((type) => type.key === formData.admissionType)?.label}
          </Pill>
        }
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,10rem)_minmax(0,1fr)] md:gap-3">
        {/* Grid 1 — admission type, stacked vertically */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-text">Admission type *</label>
          <div className="flex flex-col gap-1 rounded-xl border border-border bg-background/60 p-1">
            {admissionTypes.map((type) => {
              const active = formData.admissionType === type.key;
              return (
                <button
                  key={type.key}
                  type="button"
                  onClick={() => handleAdmissionTypeChange(type.key)}
                  aria-pressed={active}
                  className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-all ${
                    active
                      ? 'bg-surface text-primary shadow-sm ring-1 ring-primary/20'
                      : 'text-text-muted hover:bg-surface/60 hover:text-text'
                  }`}
                >
                  <type.icon size={14} className="shrink-0" />
                  <span className="truncate">{type.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Grid 2 — timing, referral and reason */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Admission time is auto-set for non-admin roles and can't be edited. */}
          {canEditAdmissionTime && (
            <div>
              <DateTimePicker
                label="Admission Date & Time *"
                value={formData.admittedAt}
                onChange={(value) => updateField('admittedAt', value)}
                error={errors.admittedAt}
                timeZone={hospitalTimezone}
              />
              {hospitalTimezone && (
                <p
                  className="mt-1 flex items-center gap-1 text-[10px] leading-4 text-text-muted"
                  title="Timezone from Settings → General"
                >
                  <Globe size={10} className="shrink-0" />
                  Times shown in {hospitalTimezone} (Settings → General)
                </p>
              )}
            </div>
          )}

          <SearchableSelect
            label="Referred By (Doctor)"
            labelClassName="block text-sm font-medium text-text mb-1.5"
            value={formData.referredById}
            onChange={(value) => updateField('referredById', value)}
            options={doctorOptions}
            placeholder="Select referring doctor (optional)..."
            loading={doctorsLoading}
            onAddNew={(query) => openOutsideDoctor('referredById', query)}
            addNewLabel="Add Outside Doctor"
          />

          {formData.admissionType === 'REFERRAL' && (
            <div className="sm:col-span-2">
              <Input
                label="Referred From"
                placeholder="Hospital / Clinic name"
                value={formData.referralDetails}
                onChange={(e) => updateField('referralDetails', e.target.value)}
              />
            </div>
          )}

          {formData.referredById && outsideDoctors.some((d) => d.id === formData.referredById) && (
            <p className="flex items-center gap-1 text-[11px] text-success sm:col-span-2">
              <CheckCircle2 size={12} />
              Outside doctor added
            </p>
          )}

          {/* Reason + Referral commission side by side: the clinical reason on
              the left, the flat IPD referral payout on the right. */}
          <div>
            <div className="mb-1.5 flex items-baseline justify-between gap-2">
              <label className="block text-sm font-medium text-text">
                Reason for Admission / Chief Complaint *
              </label>
              {errors.admissionReason && (
                <span className="shrink-0 text-right text-xs font-medium text-danger">
                  {errors.admissionReason}
                </span>
              )}
            </div>
            <textarea
              value={formData.admissionReason}
              onChange={(e) => updateField('admissionReason', e.target.value)}
              placeholder="e.g. Acute abdominal pain, Chest discomfort, Post-operative recovery..."
              rows={3}
              className={`w-full resize-y rounded-xl border bg-background/50 px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                errors.admissionReason ? 'border-danger focus:ring-danger/20' : 'border-border'
              }`}
            />
          </div>

          {/* IPD referral commission — a flat ৳ amount paid to the partner when
              the admission bill is fully settled (OPD splits a discount %). */}
          <div className="flex flex-col gap-2 rounded-xl border border-border bg-background/40 p-3">
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-1 text-sm font-medium text-text">
                Referral Commission
                <InfoHint
                  label="Referral Commission"
                  en="A flat ৳ amount paid to the referral partner for this admission — not a percentage of the bill. It accrues in Finance → Payouts when the admission bill is fully settled, and the patient still pays the full bill."
                  bn="এই ভর্তির জন্য রেফারেল পার্টনারকে প্রদেয় নির্দিষ্ট ৳ পরিমাণ (বিলের শতাংশ নয়)। ভর্তির বিল সম্পূর্ণ পরিশোধ হলে Finance → Payouts-এ এটি যোগ হবে; রোগী পুরো বিলই পরিশোধ করবেন।"
                />
              </label>
              <div className="flex items-center gap-2">
                {formData.referralId && (
                  <span className="rounded-full bg-secondary/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-secondary">
                    {referralType === 'DOCTOR'
                      ? 'Doctor'
                      : referralType === 'EMPLOYEE'
                        ? 'Employee'
                        : referralType === 'OTHER'
                          ? 'Other'
                          : 'Referral'}
                  </span>
                )}
                {formData.referralId && (
                  <button
                    type="button"
                    onClick={() => {
                      setReferralName('');
                      setReferralType('');
                      setFormData((prev) => ({
                        ...prev,
                        referralId: '',
                        referralFlatAmount: 0,
                      }));
                    }}
                    className="text-[10px] font-medium uppercase text-danger hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-[76px_minmax(0,1fr)_96px] items-end gap-1.5">
              <div>
                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text-muted">
                  Type
                </label>
                <select
                  value={referralTypeFilter}
                  onChange={(e) =>
                    setReferralTypeFilter(e.target.value as 'ALL' | 'DOCTOR' | 'EMPLOYEE' | 'OTHER')
                  }
                  className="w-full rounded-lg border border-border bg-background px-1.5 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="ALL">All</option>
                  <option value="DOCTOR">Doctor</option>
                  <option value="EMPLOYEE">Employee</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <div className="min-w-0">
                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text-muted">
                  Referral partner
                </label>
                <ReferralSearchSelect
                  value={formData.referralId}
                  onChange={(id, partner?: ReferralPartner) => {
                    setReferralName(partner?.name || '');
                    setReferralType(partner?.type || '');
                    setFormData((prev) => ({
                      ...prev,
                      referralId: id,
                      // A flat amount is not derivable from the partner's
                      // default %, so a fresh selection starts at 0.
                      referralFlatAmount: id ? prev.referralFlatAmount : 0,
                    }));
                  }}
                  selectedLabel={referralName}
                  type={referralTypeFilter}
                  placeholder="Search referral..."
                  onAddNew={(query) => {
                    setQuickReferralQuery(query);
                    setShowQuickReferralModal(true);
                  }}
                  addNewLabel="Add referral"
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-text-muted">
                  Amount (৳)
                </label>
                <ValidatedNumberInput
                  value={formData.referralFlatAmount}
                  onChange={(value) => updateField('referralFlatAmount', Math.round(value))}
                  disabled={!formData.referralId}
                  decimals={0}
                  placeholder="0"
                  className={`w-full rounded-lg border bg-background px-2 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60 ${
                    referralPayoutIsZero ? 'border-warning ring-1 ring-warning/40' : 'border-border'
                  }`}
                />
              </div>
            </div>

            {formData.referralId && referralFlatAmount > 0 && (
              <p className="text-[11px] leading-relaxed text-text-muted">
                <span className="font-medium text-text">{referralName || 'Referral partner'}</span>{' '}
                receives a flat{' '}
                <span className="font-semibold text-primary">
                  ৳{referralFlatAmount.toLocaleString()}
                </span>{' '}
                once the bill is fully settled (capped at the billed total). The patient pays the
                full bill.
              </p>
            )}

            {referralPayoutIsZero && (
              <p className="flex items-center gap-1 text-[10px] font-medium text-warning">
                <AlertTriangle size={11} />
                No payout will be created in Billing → Payouts while the amount is ৳0.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const servicesSection = (
    <div className="space-y-3 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <CardHeader
        icon={Layers}
        title="Services"
        description="Billable services billed with this admission."
        badge={
          <Pill tone={selectedServices.length > 0 ? 'primary' : 'muted'}>
            {selectedServices.length} selected
          </Pill>
        }
      />
      <div className="flex flex-col gap-2 sm:flex-row xl:shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={serviceSearch}
            onChange={(e) => setServiceSearch(e.target.value)}
            onKeyDown={serviceNav.onKeyDown}
            placeholder="Search by name, code or type..."
            className="w-full rounded-xl border border-border bg-background/50 py-2.5 pl-9 pr-4 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <select
          value={serviceTypeFilter}
          onChange={(e) => setServiceTypeFilter(e.target.value)}
          className="rounded-xl border border-border bg-background/50 px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="all">All Types</option>
          {serviceTypes.map((type: any) => (
            <option key={type.id} value={type.code}>
              {type.name}
            </option>
          ))}
        </select>
      </div>

      {servicesLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <div
          ref={serviceNav.listRef}
          className="scrollbar-thin max-h-[340px] overflow-y-auto overscroll-contain rounded-xl border border-border xl:min-h-0 xl:max-h-none xl:flex-1"
        >
          {filteredServices.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-text-muted">No services found.</p>
          ) : (
            filteredServices.map((svc: any, index: number) => {
              const isSelected = formData.serviceIds.includes(svc.id);
              const selectedQuantity = formData.serviceQuantities[svc.id] ?? 1;
              const highlighted = index === serviceNav.highlightedIndex;
              return (
                <button
                  key={svc.id}
                  type="button"
                  data-nav-index={index}
                  onMouseEnter={() => serviceNav.setHighlightedIndex(index)}
                  onClick={() => toggleService(svc.id)}
                  className={`flex w-full items-center justify-between gap-3 border-b border-border px-4 py-2.5 text-left transition-colors last:border-0 ${
                    isSelected
                      ? 'bg-primary/5'
                      : highlighted
                        ? 'bg-background ring-1 ring-inset ring-primary/40'
                        : 'hover:bg-background'
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {isSelected ? (
                      <SquareCheck size={16} className="shrink-0 text-primary" />
                    ) : (
                      <Square size={16} className="shrink-0 text-text-muted" />
                    )}
                    <div className="min-w-0">
                      <p className={`truncate text-sm font-medium ${isSelected ? 'text-primary' : 'text-text'}`}>
                        {svc.name}
                      </p>
                      <p className="truncate text-[11px] text-text-muted">
                        {[svc.code, serviceTypeName(svc.serviceType)]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {isSelected && (
                      <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                        ×{selectedQuantity}
                      </span>
                    )}
                    <span className="rounded-lg bg-background px-2 py-1 text-xs font-bold text-text">
                      {formatMoney(Number(svc.price || 0))}
                      {isQuantityBilled(svc.billingUnit) && (
                        <span className="text-[10px] font-normal text-text-muted">
                          {' '}
                          / {svc.unitLabel?.trim() || defaultUnitLabel(svc.billingUnit)}
                        </span>
                      )}
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );

  const isAdmissionReady =
    !!formData.patientId &&
    (isEmergencyAdmission || !!formData.bedId) &&
    !!formData.admittedAt &&
    !!formData.admissionReason.trim();

  const reviewSection = (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <CardHeader
        icon={CheckCircle2}
        title="Admission Summary"
        badge={
          <Pill tone={isAdmissionReady ? 'success' : 'muted'}>
            {isAdmissionReady ? 'Ready' : 'Incomplete'}
          </Pill>
        }
      />

      {createAdmission.isError && (
        <div className="flex items-start gap-2 rounded-xl border border-danger/20 bg-danger/10 p-3 text-xs text-danger">
          <AlertCircle size={15} className="mt-0.5 shrink-0" />
          <span>
            {(createAdmission.error as any)?.response?.data?.message ||
              'Failed to create admission. Please check your inputs and try again.'}
          </span>
        </div>
      )}

      <div className="shrink-0 overflow-hidden rounded-xl border border-border bg-background/40">
        {/* Patient + admission type */}
        <div className="flex items-center gap-3 bg-surface p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold uppercase text-primary ring-1 ring-primary/15">
            {patient ? patientInitials(patient) : <User size={15} />}
          </span>
          <div className="min-w-0 flex-1">
            <p className={`truncate text-sm font-semibold ${patient ? 'text-text' : 'text-text-muted'}`}>
              {patient ? joinFullName(patient) : 'No patient selected'}
            </p>
            <p className="truncate text-[11px] text-text-muted">
              {patient ? patient.mobileNumber || 'No mobile' : 'Select a patient to begin'}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
              admissionTypes.find((t) => t.key === formData.admissionType)?.color ?? ''
            }`}
          >
            {formData.admissionType.toLowerCase()}
          </span>
        </div>

        {/* Key facts */}
        <div className="grid grid-cols-2 gap-px border-t border-border bg-border">
          <SummaryCell
            icon={Bed}
            label="Bed"
            value={
              isEmergencyAdmission
                ? 'Not assigned'
                : selectedBed
                  ? selectedBed.bedNumber
                  : 'Not selected'
            }
            valueClassName={
              isEmergencyAdmission || !selectedBed ? 'text-text-muted' : 'text-text'
            }
            sub={
              isEmergencyAdmission
                ? 'Emergency — no bed required'
                : selectedBed
                  ? [
                      selectedBed.ward,
                      selectedBed.room ? `Room ${selectedBed.room}` : null,
                      `৳${Number(selectedBed.price || 0).toLocaleString()}/day`,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : undefined
            }
          />
          <SummaryCell
            icon={Stethoscope}
            label="Doctor"
            value={selectedDoctor ? selectedDoctor.name : 'Not selected'}
            valueClassName={selectedDoctor ? 'text-text' : 'text-text-muted'}
            sub={selectedDoctor ? 'Primary' : undefined}
          />
          {/* The timestamp is auto-set for non-admin roles, so hide it from them. */}
          {canEditAdmissionTime && (
            <SummaryCell
              icon={CalendarClock}
              label="Admitted"
              value={
                formData.admittedAt
                  ? new Date(formData.admittedAt).toLocaleString('en-GB', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '-'
              }
              sub={hospitalTimezone}
            />
          )}
          <SummaryCell
            icon={HeartPulse}
            label="Staff"
            value={
              selectedStaffEmployees.length > 0
                ? `${selectedStaffEmployees.length} assigned`
                : 'None'
            }
            valueClassName={selectedStaffEmployees.length > 0 ? 'text-text' : 'text-text-muted'}
            sub={
              selectedStaffEmployees.length > 0
                ? selectedStaffEmployees
                    .map((s) => s.employee?.name || 'Unknown')
                    .join(', ')
                : undefined
            }
          />
        </div>

        {/* Reason */}
        {formData.admissionReason && (
          <div className="border-t border-border bg-surface px-3 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-wide text-text-muted">Reason</p>
            <p className="line-clamp-2 text-xs leading-relaxed text-text">{formData.admissionReason}</p>
          </div>
        )}

        {/* Referral — one compact line */}
        {formData.admissionType === 'REFERRAL' &&
          (formData.referralDetails || selectedReferredBy) && (
            <div className="flex items-start gap-1.5 border-t border-border bg-surface px-3 py-2.5 text-[11px] text-text-muted">
              <FileText size={12} className="mt-0.5 shrink-0" />
              <p className="line-clamp-2 min-w-0">
                {[
                  formData.referralDetails,
                  selectedReferredBy
                    ? `via ${
                        selectedReferredBy.name ||
                        selectedReferredBy.employeeCode ||
                        selectedReferredBy.id
                      }`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          )}

        {/* IPD referral commission (flat payout) */}
        {formData.referralId && (
          <div className="flex items-start justify-between gap-2 border-t border-border bg-surface px-3 py-2.5">
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
                Referral commission
              </p>
              <p className="truncate text-xs font-medium text-text">
                {referralName || 'Selected partner'}
              </p>
              <p className="text-[10px] leading-snug text-text-muted">
                {referralFlatAmount > 0
                  ? 'Payout accrues when the bill is fully settled'
                  : 'No payout — amount is ৳0'}
              </p>
            </div>
            <span
              className={`shrink-0 text-sm font-semibold tabular-nums ${
                referralFlatAmount > 0 ? 'text-primary' : 'text-warning'
              }`}
            >
              ৳{referralFlatAmount.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* Services cart — same add/remove pattern as the OPD receipt summary */}
      <div
        className={`flex min-h-0 flex-1 flex-col gap-3 overflow-hidden rounded-xl border p-3 ${
          errors.serviceIds
            ? 'border-danger/40 bg-danger/5'
            : 'border-border bg-background/40'
        }`}
      >
        <div className="flex shrink-0 items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <ShoppingCart size={15} className="shrink-0 text-primary" />
            <h3 className="truncate text-xs font-semibold uppercase tracking-wide text-text">
              Services Cart
            </h3>
            <span
              className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                selectedServices.length > 0
                  ? 'bg-primary/10 text-primary'
                  : 'border border-border bg-surface text-text-muted'
              }`}
            >
              {selectedServices.length}
            </span>
            <span className="shrink-0 text-[10px] font-semibold text-danger">Required</span>
          </div>
          {selectedServices.length > 0 && (
            <button
              type="button"
              onClick={clearServices}
              className="shrink-0 text-[11px] font-medium text-text-muted transition-colors hover:text-danger"
            >
              Clear all
            </button>
          )}
        </div>

        {errors.serviceIds && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-danger">
            <AlertCircle size={13} />
            {errors.serviceIds}
          </p>
        )}

        {selectedServices.length === 0 ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-border px-3 py-4 text-center">
            <ShoppingCart className="mx-auto mb-1.5 h-6 w-6 text-text-muted" />
            <p className="text-xs font-medium text-text-muted">No services added yet</p>
            <p className="mt-0.5 text-[10px] leading-relaxed text-text-muted">
              At least one service is required to admit the patient. Pick services from
              the list above.
            </p>
          </div>
        ) : (
          <ul className="scrollbar-thin min-h-0 max-h-56 flex-1 space-y-1.5 overflow-y-auto overscroll-contain pr-0.5 xl:max-h-none">
            {selectedLines.map((line) => {
              const { service, quantity, pricing, priceOverridden, catalogUnitPrice } = line;
              const bounds = serviceQuantityBounds(service);
              const canOverride = canOverrideServicePrice(service);
              return (
                <li
                  key={service.id}
                  className="rounded-lg border border-border bg-surface px-2.5 py-2"
                >
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold text-text">
                        {service.name}
                        {priceOverridden && (
                          <span className="ml-1.5 rounded bg-warning/10 px-1 py-px align-middle text-[9px] font-bold uppercase tracking-wide text-warning">
                            Overwritten
                          </span>
                        )}
                      </p>
                      <p className="truncate text-[10px] text-text-muted">
                        {[
                          pricing.unitLabel
                            ? `${formatMoney(pricing.unitPrice)} / ${pricing.unitLabel}`
                            : `${formatMoney(pricing.unitPrice)} each`,
                          serviceTypeName(service.serviceType),
                          priceOverridden && catalogUnitPrice !== pricing.unitPrice
                            ? `catalog ${formatMoney(catalogUnitPrice)}`
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-text">
                      {formatMoney(pricing.amount)}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleService(service.id)}
                      className="shrink-0 rounded-lg p-1 text-text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                      title="Remove service"
                      aria-label={`Remove ${service.name}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
                      Quantity
                    </span>
                    <QuantityStepper
                      value={quantity}
                      onChange={(next) => setServiceQuantity(service.id, next)}
                      min={bounds.min}
                      max={bounds.max}
                      step={bounds.step}
                      size="sm"
                      ariaLabel={`${service.name} quantity`}
                    />
                  </div>
                  {/* Emergency admissions may overwrite the price of Emergency
                      services; every other line bills the catalog price. */}
                  {canOverride && (
                    <div className="mt-1.5 flex items-center justify-between gap-2 border-t border-dashed border-border pt-1.5">
                      <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">
                        Unit price
                        <InfoHint
                          label="Unit price"
                          en="Emergency admission: this Emergency service's price can be overwritten for this stay. Leave it at the catalog price to bill normally."
                          bn="ইমার্জেন্সি ভর্তি: এই ইমার্জেন্সি সার্ভিসের মূল্য এই ভর্তির জন্য পরিবর্তন করা যাবে। স্বাভাবিক বিলের জন্য ক্যাটালগ মূল্যই রাখুন।"
                        />
                      </span>
                      <div className="flex items-center gap-1.5">
                        {priceOverridden && (
                          <button
                            type="button"
                            onClick={() => resetServicePrice(service.id)}
                            className="rounded px-1 text-[10px] font-semibold text-primary transition-colors hover:text-primary-dark cursor-pointer"
                            title={`Back to catalog price ${formatMoney(catalogUnitPrice)}`}
                          >
                            Reset
                          </button>
                        )}
                        <div className="relative">
                          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">
                            ৳
                          </span>
                          <ValidatedNumberInput
                            value={pricing.unitPrice}
                            onChange={(next) => setServicePrice(service.id, next)}
                            max={9999999999.99}
                            className={`w-24 rounded-lg border bg-background py-1 pl-5 pr-1.5 text-right text-xs font-semibold tabular-nums text-text focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                              priceOverridden ? 'border-warning/50' : 'border-border'
                            }`}
                            title="Overwrite the unit price for this emergency service"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex shrink-0 items-center justify-between border-t border-border pt-2">
          <span className="text-sm font-semibold text-text">Total</span>
          <span className="text-lg font-bold tabular-nums text-primary">
            {formatMoney(servicesTotal)}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-5 xl:h-full xl:min-h-0">
      {/* Header */}
      <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/ipd')}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-surface text-text-muted transition-colors hover:bg-background hover:text-text cursor-pointer"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
              <Bed size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-text">New Admission</h1>
              <p className="text-sm text-text-muted">Register a new inpatient admission</p>
            </div>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          className="self-start sm:self-auto"
          onClick={() => setShowClearDialog(true)}
          disabled={isAdmissionFormEmpty(formData, selectedWardId)}
        >
          <Trash2 size={15} className="mr-1.5" />
          Start fresh
        </Button>
      </div>

      {hasSavedDraft && (
        <div className="flex shrink-0 items-start gap-3 rounded-xl border border-warning/20 bg-warning/10 p-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <div className="flex-1 text-sm text-warning">
            An unfinished admission draft was restored. Continue where you left off, or start fresh.
          </div>
          <button
            type="button"
            onClick={() => setShowClearDialog(true)}
            className="shrink-0 text-sm font-semibold text-warning underline-offset-2 hover:underline cursor-pointer"
          >
            Clear
          </button>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-5 xl:min-h-0 xl:flex-1"
      >
        {submitAttempted && Object.keys(errors).length > 0 && (
          <div className="flex shrink-0 items-start gap-2 rounded-xl border border-danger/20 bg-danger/10 p-3 text-sm text-danger">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>Please complete the required fields highlighted below.</span>
          </div>
        )}

        {/* Admission setup: patient, bed and clinical team in one panel */}
        <section className="shrink-0 overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-border bg-background/40 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary text-white shadow-sm">
                <Bed size={15} />
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold leading-tight text-text">Admission setup</h2>
                <p className="truncate text-[11px] leading-tight text-text-muted">
                  Patient, bed and clinical team
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5" aria-hidden>
              {[
                !!formData.patientId && !activeAdmission,
                isEmergencyAdmission || !!formData.bedId,
                formData.staffAssignments.length > 0 || !!formData.doctorId,
              ].map((done, index) => (
                <span
                  key={index}
                  className={`h-1.5 rounded-full transition-all ${
                    done ? 'w-7 bg-primary' : 'w-4 bg-border'
                  }`}
                />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 divide-y divide-border xl:grid-cols-3 xl:divide-x xl:divide-y-0">
            <div className="flex min-w-0 flex-col p-4">{patientStep}</div>
            <div className="flex min-w-0 flex-col p-4">{bedSection}</div>
            <div className="flex min-w-0 flex-col p-4">{teamSection}</div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-5 xl:min-h-[26rem] xl:flex-1 xl:grid-cols-12 xl:grid-rows-1">
          {/* Left: admission details + service list (only the service list scrolls) */}
          <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm xl:col-span-8 xl:flex xl:min-h-0 xl:flex-col">
            <div className="p-5 xl:min-h-0 xl:overflow-y-auto xl:overscroll-contain scrollbar-thin">
              {detailsStep}
            </div>
            <div className="border-t border-border p-5 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
              {servicesSection}
            </div>
          </div>

          {/* Right: summary + submit — fills the second row beside the details */}
          <div className="xl:col-span-4 xl:min-h-0">
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
              <div className="flex min-h-0 flex-1 flex-col p-5">{reviewSection}</div>

              <div className="space-y-2.5 border-t border-border bg-background/40 p-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Admit Patient'}
                </Button>
                <p className="text-center text-[11px] text-text-muted">
                  All details are reviewed on this page before admitting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      <OutsideDoctorModal
        open={showOutsideDoctorModal}
        initialName={outsideDoctorQuery}
        initialPhone={/^\d+$/.test(outsideDoctorQuery) ? outsideDoctorQuery : ''}
        onClose={() => setShowOutsideDoctorModal(false)}
        onCreated={handleOutsideDoctorCreated}
      />

      {/* ===== DUPLICATE / ACTIVE ADMISSION GATE ===== */}
      {activeAdmission && showActiveAdmissionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowActiveAdmissionModal(false)}
        >
          <div
            className="bg-surface border border-border rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-danger/10 shrink-0">
                  <AlertTriangle size={20} className="text-danger" />
                </div>
                <div className="space-y-1 min-w-0">
                  <h3 className="text-base font-semibold text-text">Patient already admitted</h3>
                  <p className="text-sm text-text-muted">
                    {activeAdmission.patient
                      ? joinFullName(activeAdmission.patient)
                      : 'This patient'}{' '}
                    already has an active admission that has not been discharged yet. A new
                    admission cannot be created until the existing stay is closed.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-background divide-y divide-border">
                <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="text-sm text-text-muted">Status</span>
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700">
                    {activeAdmission.status?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="text-sm text-text-muted">Bed</span>
                  <span className="text-sm font-semibold text-text text-right">
                    {activeAdmission.bed?.bedNumber || '—'}
                    {activeAdmission.bed?.ward ? ` · ${activeAdmission.bed.ward}` : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="text-sm text-text-muted">Doctor</span>
                  <span className="text-sm font-semibold text-text text-right">
                    {activeAdmission.doctor?.name || '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span className="text-sm text-text-muted">Admitted</span>
                  <span className="text-sm font-semibold text-text text-right">
                    {formatOrdinalDateTimeLong(activeAdmission.admittedAt)}
                  </span>
                </div>
                {activeAdmission.admissionReason && (
                  <div className="px-4 py-2.5">
                    <p className="text-sm text-text-muted mb-0.5">Reason</p>
                    <p className="text-sm text-text">{activeAdmission.admissionReason}</p>
                  </div>
                )}
              </div>

              <p className="text-xs text-text-muted">
                Discharge the current admission (and clear its bill) before admitting this
                patient again, or select a different patient.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
              <button
                type="button"
                onClick={() => setShowActiveAdmissionModal(false)}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowActiveAdmissionModal(false);
                  navigate(`/ipd/admissions/${activeAdmission.id}`, {
                    state: { from: '/ipd/new' },
                  });
                }}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors flex items-center gap-1.5"
              >
                <Eye size={15} />
                View Admission
              </button>
            </div>
          </div>
        </div>
      )}

      <QuickPatientModal
        open={showQuickPatientModal}
        onClose={() => setShowQuickPatientModal(false)}
        initialQuery={patientQuery}
        onCreated={(patient) => {
          setFormData((prev) => ({ ...prev, patientId: patient.id, visitId: '', bedId: '' }));
          setSelectedWardId('');
          setTouched((prev) => ({ ...prev, patientId: true, visitId: true }));
        }}
        onOpenFullForm={() => navigate('/patients/new')}
      />

      {/* Quick-create a referral partner without leaving the admission form */}
      <QuickReferralModal
        open={showQuickReferralModal}
        initialQuery={quickReferralQuery}
        onClose={() => setShowQuickReferralModal(false)}
        onCreated={(partner) => {
          setShowQuickReferralModal(false);
          setReferralName(partner?.name || '');
          setReferralType(partner?.type || '');
          setFormData((prev) => ({
            ...prev,
            referralId: partner?.id || '',
            referralFlatAmount: partner?.id ? prev.referralFlatAmount : 0,
          }));
        }}
      />

      <ConfirmDialog
        open={showReferralZeroWarning}
        title="No referral payout will be created"
        description={
          <div className="space-y-2">
            <p>
              <span className="font-medium text-text">
                {referralName || 'A referral partner'}
              </span>{' '}
              is selected but the flat commission is <span className="font-semibold">৳0</span>.
              This admission will not appear in Billing → Payouts for that partner.
            </p>
            <p>Set an amount, or save the admission without a payout.</p>
          </div>
        }
        confirmLabel="Save without payout"
        cancelLabel="Go back & fix"
        variant="primary"
        onConfirm={submitAdmission}
        onCancel={() => setShowReferralZeroWarning(false)}
      />

      <ConfirmDialog
        open={showClearDialog}
        title="Start a brand new admission?"
        description="This clears all data currently entered in this form, including the saved draft. This cannot be undone."
        confirmLabel="Clear & start fresh"
        variant="danger"
        onConfirm={resetForm}
        onCancel={() => setShowClearDialog(false)}
      />

      <AdmissionInvoiceModal
        open={showBillingModal}
        admission={createdAdmission}
        patientName={patient ? joinFullName(patient) : undefined}
        onDone={() => {
          setShowBillingModal(false);
          navigate('/ipd', { replace: true });
        }}
        onViewAdmission={() => {
          if (!createdAdmission?.id) {
            navigate('/ipd', { replace: true });
            return;
          }
          setShowBillingModal(false);
          navigate(`/ipd/admissions/${createdAdmission.id}`, { state: { from: '/ipd/new' } });
        }}
        onViewInvoices={() => {
          setShowBillingModal(false);
          navigate('/billing/invoices');
        }}
      />
    </div>
  );
}
